import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSVariables = () => {
    const declaringVariablesCode = `// Declaring variables with modern syntax
let userName = 'Alice';      // String literal
const userAge = 25;          // Number literal
let isVerified = true;       // Boolean literal

console.log(userName);       // Output: Alice
console.log(userAge);        // Output: 25`;

    const letScopeCode = `// 'let' is block-scoped
let score = 100;
score = 200;              // Reassignment is allowed
console.log(score);       // Output: 200

if (true) {
    let internalMessage = 'Hello from inside';
    console.log(internalMessage); // Works here
}
console.log(internalMessage);     //  ReferenceError: Not defined in this scope`;

    const constBestPracticeCode = `// 'const' requires initialization and remains constant
const PI = 3.14159;
PI = 3.14;                //  TypeError: Assignment to constant variable

// Objects and Arrays are reference types
const userProfile = { name: 'Alice', age: 25 };
userProfile.age = 26;           // Works - modifying a property
// userProfile = { name: 'Bob' };  //  Error - reassignment of the reference`;

    const varPitfallsCode = `// 'var' is function-scoped and hoisted
if (true) {
    var leakedVariable = 'I am everywhere';
}
console.log(leakedVariable); // Works: 'I am everywhere' (Problematic!)

// Redeclaration is dangerously allowed
var count = 5;
var count = 10; // No error, just overrides. Hard to debug.`;

    const scopeExampleCode = `// Global vs Block Scope
const globalConfig = 'PROD';

function initialize() {
    let localKey = 'ABC-123';
    
    if (globalConfig === 'PROD') {
        let secretToken = 'Shhh!'; 
        // localKey is accessible here
        // globalConfig is accessible here
    }
    // secretToken is NOT accessible here
} `;

    const hoistingCode = `// Hoisting with 'var'
console.log(myVar); // Output: undefined (no error)
var myVar = 'exists';

// Temporal Dead Zone with 'let'
// console.log(myLet); //  ReferenceError
let myLet = 'safe';`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">JavaScript Fundamentals</div>
                <h1>Variables & Scope (let, const, var)</h1>
                <p className="hero-lead">
                    Master JavaScript's memory containers. Understand the architectural differences between
                    let, const, and var, and learn how the JS engine handles scope and hoisting.
                </p>
            </div>

            <div className="content-section">
                <h2>What are Variables in JavaScript?</h2>
                <p>
                    In computer science, a variable is a symbolic name for a reference to a memory location.
                    In JavaScript, you use variables to store data that your program needs to process.
                    Think of them as "labels" that point to values in your computer's RAM.
                </p>
                <ProTip><strong> Engineer's Insight:</strong> In modern browsers, variable management is
                    handled by the <strong>Garbage Collector</strong>. When a variable goes out of scope and
                    is no longer reachable, the memory it occupied is eventually reclaimed automatically.</ProTip>
                <CodeBlock language="javascript" code={declaringVariablesCode} />
            </div>

            <div className="content-section">
                <h2>The Evolution of Variable Declaration</h2>
                <p>
                    For many years, <code>var</code> was the only way to declare variables. However,
                    due to its confusing scoping rules, ES6 (2015) introduced <code>let</code> and <code>const</code>,
                    which are now the industrial standard for writing clean, predictable code.
                </p>

                <h3>1. let: The Standard Variable</h3>
                <p>
                    Use <code>let</code> when you expect a variable's value to change over time (reassignment).
                    The most important feature of <code>let</code> is <strong>Block Scoping</strong>.
                    A block is any code wrapped in <code>{"{"} {"}"}</code> braces, such as if-statements or loops.
                </p>
                <CodeBlock language="javascript" code={letScopeCode} />

                <h3>2. const: Immutable References</h3>
                <p>
                    The <code>const</code> keyword is used for values that should not be reassigned.
                    In professional development, <strong>const is the default choice</strong>. By using
                    const, you signal to other developers (and the JS engine) that this value is stable.
                </p>
                <p>
                    <em>Crucial Note:</em> For objects and arrays, <code>const</code> prevents the variable
                    from pointing to a <strong>new</strong> object, but it does NOT make the object's
                    internal properties immutable.
                </p>
                <CodeBlock language="javascript" code={constBestPracticeCode} />

                <h3>3. var: The Legacy Pattern (Avoid!)</h3>
                <p>
                    The <code>var</code> keyword is function-scoped rather than block-scoped. This often
                    leads to "variable leakage" where a variable declared inside an <code>if</code> or
                    <code>for</code> block is accessible outside of it, causing hard-to-track bugs.
                </p>
                <CodeBlock language="javascript" code={varPitfallsCode} />
            </div>

            <div className="content-section">
                <h2>Deep Dive into Scope</h2>
                <p>
                    <strong>Scope</strong> defines the visibility and lifetime of variables. JavaScript
                    utilizes "Lexical Scoping," meaning the access to variables is determined by
                    the physical location of the code within your files.
                </p>
                <ul>
                    <li><strong>Global Scope:</strong> Variables declared outside any function or block. They are accessible throughout the entire application. Be careful: overusing global variables can lead to name collisions.</li>
                    <li><strong>Local/Function Scope:</strong> Variables declared inside a function are private to that function.</li>
                    <li><strong>Block Scope:</strong> Introduced by <code>let</code> and <code>const</code>. Variables only exist within the closest pair of curly braces.</li>
                </ul>
                <CodeBlock language="javascript" code={scopeExampleCode} />
            </div>

            <div className="content-section">
                <h2>The Temporal Dead Zone (TDZ) & Hoisting</h2>
                <p>
                    Have you ever wondered why you can sometimes use a variable before it's declared?
                    This is called <strong>Hoisting</strong>. The JS engine moves declarations to
                    the top of the script during the "creation phase."
                </p>
                <p>
                    However, <code>let</code> and <code>const</code> are safer because they enter a
                    <strong>Temporal Dead Zone</strong>. If you try to access them before the actual
                    declaration line, the program will crash with a clear error, preventing logical bugs.
                </p>
                <CodeBlock language="javascript" code={hoistingCode} />
            </div>

            <div className="content-section">
                <h2>Production Best Practices</h2>
                <p>
                    To write high-quality, enterprise-ready JavaScript, follow these naming and
                    declaration conventions:
                </p>
                <div className="best-practices-grid">
                    <div className="bp-card">
                        <h4>Use camelCase</h4>
                        <p>Variables and functions should start with a lowercase letter and use uppercase for subsequent words: <code>calculateTotalPrice</code>.</p>
                    </div>
                    <div className="bp-card">
                        <h4>Descriptive Naming</h4>
                        <p>Avoid single letters like <code>x</code> or <code>y</code>. Use names that describe the content: <code>userAuthenticationToken</code>.</p>
                    </div>
                </div>
            </div>

            <KeyFacts><ul>
                    <li>Always default to <code>const</code>.</li>
                    <li>Only use <code>let</code> if the value must change.</li>
                    <li>Completely abandon <code>var</code> in new projects.</li>
                    <li>Keep your scope as narrow as possible (Principle of Least Privilege).</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSVariables;



